import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Typography,
  Select,
  message,
  Card,
  Button,
  Input,
  Tag,
  Tooltip,
  Alert,
  Space,
  Divider,
  Modal,
  Form,
  Row,
  Col,
  Pagination,
  Checkbox,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  BankOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  TrophyOutlined,
  DollarOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  SafetyOutlined,
  GlobalOutlined,
  FormOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import "./IssuerList.css";
import "../Admin/AdminPages.css";
import { usePolicy } from "../../context/PolicyContext";
import { hasPolicyPermission } from "../../utils/policyUtils";
import { isAdmin } from "../../utils/roleUtils";

const { Title, Text, Paragraph } = Typography;
const { Column } = Table;
const { Option } = Select;
const { Search } = Input;

function IssuerList() {
  const navigate = useNavigate();
  const { policies } = usePolicy();
  console.log("policies", policies);
  const canEdit = hasPolicyPermission(policies, "api:issuer:edit");
  const canDelete = hasPolicyPermission(policies, "api:issuer:delete");
  console.log("canEdit:", canEdit, "canDelete:", canDelete);
  const { isDark } = useContext(ThemeContext);
  const [issuerData, setIssuerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedSector, setSelectedSector] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingIssuer, setEditingIssuer] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [form] = Form.useForm();
  const [unclassifiedOnly, setUnclassifiedOnly] = useState(false);

  // Watch ownership type for conditional sub-ownership dropdown
  const ownershipType = Form.useWatch('typeOfIssuerBasedOnOwnership', form);

  // State for cascading dropdowns in edit form
  const [selectedMacroeconomicSector, setSelectedMacroeconomicSector] = useState(null);
  const [selectedFormSector, setSelectedFormSector] = useState(null);
  const [selectedFormIndustry, setSelectedFormIndustry] = useState(null);

  // Hierarchical data fetched from API
  const [hierarchicalData, setHierarchicalData] = useState({});

  // Convert enum value to display name (e.g., "TERTIARY_INDUSTRIES" -> "Tertiary Industries")
  const formatEnumToDisplay = (enumValue) => {
    if (!enumValue) return "";

    // Special cases for acronyms and specific terms
    const specialCases = {
      "NBFC": "NBFC",
      "PSU": "PSU"
    };

    if (specialCases[enumValue]) {
      return specialCases[enumValue];
    }

    // Replace underscores with spaces and capitalize each word
    return enumValue
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .replace(/\bAnd\b/g, '&'); // Replace " And " with " & "
  };

  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  // Fetch issuer data
  const fetchIssuerData = async (page = 1, pageSize = 50, fetchUnclassified = unclassifiedOnly) => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };
      const endpoint = fetchUnclassified
        ? `${baseUrl}/master-issuer/without-classification?page=${page - 1}&size=${pageSize}`
        : `${baseUrl}/master-issuer/all?page=${page - 1}&size=${pageSize}`;

      const response = await apiClient.get(endpoint, { headers });

      console.log("Issuer Data Response:", response.data);

      if (response.data.statusCode === 200) {
        setIssuerData(response.data.data.content || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.data.totalElements || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Error fetching issuer data");
      message.error("Error fetching issuer data");
      console.error("Error fetching issuer data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch hierarchy data from backend
  const fetchHierarchyData = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const response = await apiClient.get(
        `${baseUrl}/sector-industry-classification/structure`
      );
      setHierarchicalData(response.data);
    } catch (error) {
      console.error("Failed to load hierarchy data:", error);
      message.error("Failed to load sector hierarchy data");
    }
  };

  // On mount - fetch both hierarchy data and issuer data
  useEffect(() => {
    fetchIssuerData();
    fetchHierarchyData();
  }, []);

  // Handle pagination change
  const handleTableChange = (pagination) => {
    fetchIssuerData(pagination.current, pagination.pageSize);
  };

  const handleToggleUnclassified = (checked) => {
    setUnclassifiedOnly(checked);
    fetchIssuerData(1, pagination.pageSize, checked);
  };

  // Filtered data based on search and filters
  const filteredData = issuerData.filter((item) => {
    // Use classification object if available, otherwise fallback to direct fields
    const sector = item.classification?.sector || '';
    const industry = item.classification?.industry || '';

    const matchesSearch =
      item.issuer?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.corporateIdentityNumber
        ?.toLowerCase()
        .includes(searchText.toLowerCase()) ||
      sector?.toLowerCase().includes(searchText.toLowerCase()) ||
      industry?.toLowerCase().includes(searchText.toLowerCase());

    const matchesSector =
      selectedSector === "all" || sector === selectedSector;
    const matchesIndustry =
      selectedIndustry === "all" || industry === selectedIndustry;

    return matchesSearch && matchesSector && matchesIndustry;
  });

  // Get unique sectors and industries for filters (using classification if available)
  const sectors = [
    ...new Set(issuerData.map((item) => item.classification?.sector || '').filter(Boolean)),
  ];
  const industries = [
    ...new Set(issuerData.map((item) => item.classification?.industry || '').filter(Boolean)),
  ];


  const handleRefresh = () => {
    fetchIssuerData(pagination.current, pagination.pageSize);
  };

  const handleExport = () => {
    // Export functionality
    console.log("Export functionality to be implemented");
    message.info("Export functionality coming soon");
  };

  const getStatusColor = (status) => {
    if (status === "Listed") return "green";
    if (status === "Unlisted") return "red";
    return "default";
  };

  const getSectorColor = (sector) => {
    const colors = {
      "Financial Services": "blue",
      Manufacturing: "purple",
      Technology: "cyan",
      Healthcare: "green",
      Energy: "orange",
      Power: "yellow",
      Chemicals: "brown",
      "Real Estate": "magenta",
    };
    return colors[sector] || "default";
  };

  const getIndustryColor = (industry) => {
    const colors = {
      Finance: "blue",
      Banks: "green",
      Insurance: "purple",
      Power: "yellow",
      Healthcare: "cyan",
      Manufacturing: "orange",
    };
    return colors[industry] || "default";
  };

  const handleViewDetails = (record) => {
    console.log("View details for:", record);

    // Check if user is admin and navigate accordingly
    if (isAdmin()) {
      navigate("/admin/issuer/details", {
        state: { cin: record.corporateIdentityNumber },
      });
    } else {
      navigate("/d/issuer/details", {
        state: { cin: record.corporateIdentityNumber },
      });
    }
  };

  const handleEdit = (record) => {
    setEditingIssuer(record);

    // Use classification object if available, otherwise fallback to p_* fields
    const classification = record.classification;
    const macroeconomicSector = classification?.macroeconomicSector || '';
    const sector = classification?.sector || '';
    const industry = classification?.industry || '';
    const basicIndustry = classification?.basicIndustry || '';

    // Normalize ownership type value to match Select options
    let normalizedOwnershipType = record.typeOfIssuerBasedOnOwnership;
    if (normalizedOwnershipType === "Non PSU" || normalizedOwnershipType === "Non_PSU") {
      normalizedOwnershipType = "Non_PSU";
    } else if (normalizedOwnershipType === "PSU") {
      normalizedOwnershipType = "PSU";
    }

    // Set state variables for cascading dropdowns
    setSelectedMacroeconomicSector(macroeconomicSector);
    setSelectedFormSector(sector);
    setSelectedFormIndustry(industry);

    form.setFieldsValue({
      issuer: record.issuer,
      corporateIdentityNumber: record.corporateIdentityNumber,
      legalEntityIdentifier: record.legalEntityIdentifier,
      typeOfIssuerBasedOnOwnership: normalizedOwnershipType,
      typeofSubOwnership: record.typeofSubOwnership,
      macroeconomicSector: record.macroeconomicSector,
      sector: record.sector,
      industry: record.industry,
      basicIndustry: record.basicIndustry,
      p_macroeconomicSector: macroeconomicSector,
      p_sector: sector,
      p_industry: industry,
      p_basicIndustry: basicIndustry,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    console.log("Values:", values);
    setEditLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };

      // Merge form values with hidden fields from editingIssuer
      const payload = {
        ...values,
        macroeconomicSector: editingIssuer.macroeconomicSector,
        sector: editingIssuer.sector,
        industry: editingIssuer.industry,
        basicIndustry: editingIssuer.basicIndustry,
      };

      console.log("Payload with hidden fields:", payload);

      const response = await apiClient.put(
        `${baseUrl}/master-issuer/update/${encodeURIComponent(editingIssuer.corporateIdentityNumber)}`,
        payload,
        { headers }
      );

      if (response.data.statusCode === 200) {
        message.success("Issuer updated successfully");
        setEditModalVisible(false);
        setEditingIssuer(null);
        form.resetFields();
        // Refresh the data
        fetchIssuerData(pagination.current, pagination.pageSize);
      } else {
        throw new Error(response.data.message || "Failed to update issuer");
      }
    } catch (error) {
      message.error(
        "Error updating issuer: " +
        (error.response?.data?.message || error.message)
      );
      console.error("Error updating issuer:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingIssuer(null);
    form.resetFields();
    // Reset cascading dropdown state
    setSelectedMacroeconomicSector(null);
    setSelectedFormSector(null);
    setSelectedFormIndustry(null);
  };

  // Cascading dropdown handlers
  const handleMacroeconomicSectorChange = (value) => {
    console.log("Macroeconomic Sector changed to:", value);
    setSelectedMacroeconomicSector(value);
    setSelectedFormSector(null);
    setSelectedFormIndustry(null);
    // Reset dependent fields when parent changes
    form.setFieldsValue({
      p_sector: undefined,
      p_industry: undefined,
      p_basicIndustry: undefined,
    });
  };

  const handleSectorChange = (value) => {
    console.log("Sector changed to:", value);
    setSelectedFormSector(value);
    setSelectedFormIndustry(null);
    // Reset dependent fields when parent changes
    form.setFieldsValue({
      p_industry: undefined,
      p_basicIndustry: undefined,
    });
  };

  const handleIndustryChange = (value) => {
    console.log("Industry changed to:", value);
    setSelectedFormIndustry(value);
    // Reset dependent field when parent changes
    form.setFieldsValue({
      p_basicIndustry: undefined,
    });
  };

  // Get available options for each dropdown (enum values with display names)
  const getMacroeconomicSectorOptions = () => {
    return Object.keys(hierarchicalData).map(key => ({
      label: formatEnumToDisplay(key),  // Show formatted display name in UI
      value: key                         // Send enum value to backend
    }));
  };

  const getSectorOptions = () => {
    console.log("Getting sector options for:", selectedMacroeconomicSector);
    if (!selectedMacroeconomicSector || !hierarchicalData[selectedMacroeconomicSector]) {
      return [];
    }
    const options = Object.keys(hierarchicalData[selectedMacroeconomicSector]).map(key => ({
      label: formatEnumToDisplay(key),  // Show formatted display name in UI
      value: key                         // Send enum value to backend
    }));
    console.log("Sector options:", options);
    return options;
  };

  const getIndustryOptions = () => {
    console.log("Getting industry options for:", selectedMacroeconomicSector, selectedFormSector);
    if (!selectedMacroeconomicSector || !selectedFormSector ||
      !hierarchicalData[selectedMacroeconomicSector] ||
      !hierarchicalData[selectedMacroeconomicSector][selectedFormSector]) {
      return [];
    }

    const options = Object.keys(hierarchicalData[selectedMacroeconomicSector][selectedFormSector]).map(key => ({
      label: formatEnumToDisplay(key),  // Show formatted display name in UI
      value: key                         // Send enum value to backend
    }));
    console.log("Industry options:", options);
    return options;
  };

  const getBasicIndustryOptions = () => {
    console.log("Getting basic industry options for:", selectedMacroeconomicSector, selectedFormSector, selectedFormIndustry);
    if (!selectedMacroeconomicSector || !selectedFormSector || !selectedFormIndustry ||
      !hierarchicalData[selectedMacroeconomicSector] ||
      !hierarchicalData[selectedMacroeconomicSector][selectedFormSector] ||
      !hierarchicalData[selectedMacroeconomicSector][selectedFormSector][selectedFormIndustry]) {
      return [];
    }

    const basicIndustries = hierarchicalData[selectedMacroeconomicSector][selectedFormSector][selectedFormIndustry];
    const options = basicIndustries
      .filter(industry => industry) // Filter out empty strings
      .map(industry => ({
        label: formatEnumToDisplay(industry),  // Show formatted display name in UI
        value: industry                         // Send enum value to backend
      }));
    console.log("Basic industry options:", options);
    return options;
  };


  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const tableColumns = [
    {
      title: "Issuer Name",
      dataIndex: "issuer",
      key: "issuer",
      align: "left",
      render: (text, record) => {
        const createdAt = formatDate(record.createdAt);
        const updatedAt = formatDate(record.updatedAt);
        const tooltipContent = (
          <div>
            <div>
              <b>Created At:</b> {createdAt}
            </div>
            <div>
              <b>Updated At:</b> {updatedAt}
            </div>
          </div>
        );
        return (
          <Tooltip
            title={tooltipContent}
            placement="topLeft"
            overlayClassName="custom-tooltip"
          >
            <div className="company-name">{text || "N/A"}</div>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.issuer || "").localeCompare(b.issuer || ""),
    },
    {
      title: 'Ownership Type',
      dataIndex: "typeOfIssuerBasedOnOwnership",
      key: "typeOfIssuerBasedOnOwnership",
      render: (text) => (
        <Tag>{text}</Tag>
        // <Tag color={text === "Non_PSU" ? "orange" : "green"}>{text && text.includes("Non_PSU") ? "Non PSU" : "PSU"}</Tag>
      ),
    },
    {
      title: "Sub Ownership Type",
      dataIndex: "typeofSubOwnership",
      key: "typeofSubOwnership",
      render: (text) => (
        <Tag>{text}</Tag>
      ),
    },
    {
      title: "Sector",
      dataIndex: "p_sector",
      key: "sector",

      render: (text, record) => {
        // Use classification object if available, otherwise fallback to p_sector
        const sector = record.classification?.sector || '';
        return <Tag color={getSectorColor(sector)}>{formatEnumToDisplay(sector) || "N/A"}</Tag>;
      },
      filters: sectors.map((sector) => ({ text: sector, value: sector })),
      onFilter: (value, record) => {
        const sector = record.classification?.sector || '';
        return sector === value;
      },
    },
    {
      title: "Industry",
      dataIndex: "p_industry",
      key: "industry",

      render: (text, record) => {
        // Use classification object if available, otherwise fallback to p_industry
        const industry = record.classification?.industry || '';
        return (
          <Tag color={getIndustryColor(industry)}>{formatEnumToDisplay(industry) || "N/A"}</Tag>
        );
      },
      filters: industries.map((industry) => ({
        text: industry,
        value: industry,
      })),
      onFilter: (value, record) => {
        const industry = record.classification?.industry || '';
        return industry === value;
      },
    },

    {
      title: "Basic Industry",
      dataIndex: "p_basicIndustry",
      key: "p_basicIndustry",

      render: (text, record) => {
        // Use classification object if available, otherwise fallback to p_basicIndustry
        const basicIndustry = record.classification?.basicIndustry || '';
        return (
          <Tag color={basicIndustry === "PSU" ? "green" : "orange"}>{formatEnumToDisplay(basicIndustry) || "N/A"}</Tag>
        );
      },
    },


    {
      title: "Action",
      key: "",

      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              type="default"
              size="small"
              style={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                borderColor: '#1890ff',
                color: '#1890ff',
                borderRadius: 8
              }}
            />
          </Tooltip>
          {
            canEdit && (
              <Tooltip title="Edit Issuer">
                <Button
                  icon={<FormOutlined />}
                  onClick={() => handleEdit(record)}
                  type="default"
                  size="small"
                  style={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    borderColor: '#52c41a',
                    color: '#52c41a',
                    borderRadius: 8
                  }}
                />
              </Tooltip>
            )
          }
        </Space>
      ),
    },
  ];

  return (
    <div className={`admin-page ${isDark ? "dark" : ""}`}>
      {/* Hero Section */}
      <div className="admin-header-banner">
        <div className="hero-content">
          <Title level={1} className="hero-title-news">
            Issuer List
          </Title>
          <Paragraph className="hero-description">
            Comprehensive bond issuer information with detailed sectors, industries, and analytics for informed investment decisions
          </Paragraph>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', padding: '0 24px' }}>
        <Space size="large">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox
              checked={unclassifiedOnly}
              onChange={(e) => handleToggleUnclassified(e.target.checked)}
            >
              <Text strong>Show Unclassified Only</Text>
            </Checkbox>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Space>
      </div>


      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Data Display */}
      <Card className="admin-main-card">
        <Table
          columns={tableColumns}
          dataSource={filteredData}
          rowKey="corporateIdentityNumber"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
          className="admin-table"
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Issuer"
        open={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
          initialValues={editingIssuer}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="issuer"
                label="Issuer Name"
                rules={[
                  { required: true, message: "Please enter issuer name" },
                ]}
              >
                <Input placeholder="Enter issuer name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="corporateIdentityNumber"
                label="Corporate Identity Number"
                rules={[{ required: true, message: "Please enter CIN" }]}
              >
                <Input placeholder="Enter CIN" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="legalEntityIdentifier"
                label="Legal Entity Identifier"
              >
                <Input placeholder="Enter legal entity identifier" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="typeOfIssuerBasedOnOwnership"
                label="Ownership Type"
                rules={[
                  { required: true, message: "Please select ownership type" },
                ]}
              >
                <Select
                  placeholder="Select ownership type"
                  onChange={() => {
                    // Reset sub-ownership when ownership type changes
                    form.setFieldsValue({ typeofSubOwnership: undefined });
                  }}
                >
                  <Option value="PSU">PSU</Option>
                  <Option value="Non_PSU">Non PSU</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="typeofSubOwnership"
                label="Sub Ownership Type"
                dependencies={['typeOfIssuerBasedOnOwnership']}
                rules={[
                  { required: true, message: "Please select sub ownership type" },
                ]}
              >
                <Select
                  placeholder="Select sub ownership type"
                  disabled={!ownershipType}
                >
                  {ownershipType === "PSU" ? (
                    <>
                      <Option value="GOVERNMENT_COMPANY">Government Company</Option>
                      <Option value="CENTRAL_GOVERNMENT">Central Government</Option>
                      <Option value="STATE_GOVERNMENT">State Government</Option>
                      <Option value="INSTITUTIONAL_OWNED">Institutional Owned</Option>
                    </>
                  ) : ownershipType === "Non_PSU" ? (
                    <>
                      <Option value="DOMESTIC">Domestic</Option>
                      <Option value="FOREIGN">Foreign</Option>
                      <Option value="INSTITUTIONAL_OWNED">Institutional Owned</Option>
                      <Option value="WIDELY_HELD">Widely Held</Option>
                    </>
                  ) : null}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="p_macroeconomicSector"
                label="Macroeconomic Sector"
                rules={[{ required: true, message: "Please select macroeconomic sector" }]}
              >
                <Select
                  placeholder="Select macroeconomic sector"
                  onChange={handleMacroeconomicSectorChange}
                  allowClear
                >
                  {getMacroeconomicSectorOptions().map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="p_sector"
                label="Sector"
                rules={[{ required: true, message: "Please select sector" }]}
              >
                <Select
                  placeholder="Select sector"
                  onChange={handleSectorChange}
                  allowClear
                  disabled={!selectedMacroeconomicSector}
                >
                  {getSectorOptions().map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="p_industry"
                label="Industry"
                rules={[{ required: true, message: "Please select industry" }]}
              >
                <Select
                  placeholder="Select industry"
                  onChange={handleIndustryChange}
                  allowClear
                  disabled={!selectedFormSector}
                >
                  {getIndustryOptions().map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="p_basicIndustry"
                label="Basic Industry"
                rules={[
                  { required: true, message: "Please select basic industry" },
                ]}
              >
                <Select
                  placeholder="Select basic industry"
                  allowClear
                  disabled={!selectedFormIndustry}
                >
                  {getBasicIndustryOptions().map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>


          <div style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={handleEditCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Update Issuer
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

    </div>
  );
}

export default IssuerList;
